<template>
  <div class="newsletter-signup-from__newsletter-wrapper">
    <SmoothReflow>
      <div
        v-if="$newsletterSignup_isRequestStateSuccess"
        class="newsletter-signup-from__request-msg popup__request-msg--success"
        v-data-test-id="'newsletter-signup-success'"
        v-html="successMessage"
      ></div>
    </SmoothReflow>
    <SmoothReflow>
      <div
        v-if="$data.$newsletterSignup_errorMessage || $newsletterSignup_isRequestStateError"
        :class="errorMessageClasses"
        v-data-test-id="'newsletter-signup-error'"
        v-html="$data.$newsletterSignup_errorMessage || errorMessage"
      ></div>
    </SmoothReflow>
    <SmoothReflow>
      <form
        v-if="!$newsletterSignup_isRequestStateSuccess"
        class="newsletter-signup-from__newsletter-form"
        v-data-test-id="'newsletter-signup-form'"
        @submit.prevent="$newsletterSignup_submit(formData)"
      >
        <div class="newsletter-signup-from__input-wrap">
          <input
            v-model="email"
            class="newsletter-signup-from__input"
            :class="`newsletter-signup-from__input--${theme}`"
            :placeholder="inputPlaceholder"
            v-data-test-id="'newsletter-signup-input'"
            @keyup="event => this.$newsletterSignup_handleEmailInputChange({ event, ...formData })"
          />
        </div>
        <div class="newsletter-signup-from__cta-wrap">
          <Button
            type="submit"
            :isSubmitting="$newsletterSignup_isRequestStateSubmitting"
            :color="buttonColor"
            v-data-test-id="'newsletter-signup-submit'"
          >
            {{ submitButtonText }}
          </Button>
        </div>
      </form>
    </SmoothReflow>
  </div>
</template>

<script>
import { includesValue } from '@/helpers/object'
import newsletterSignupMixin, { SOURCES } from '@/mixins/newsletter-signup'
import SmoothReflow from '@/components/_shared/SmoothReflow.vue'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'

export const THEMES = {
  light: 'light',
  dark: 'dark',
}

export default {
  name: 'NewsletterSignupForm',
  components: {
    SmoothReflow,
    Button,
  },
  mixins: [newsletterSignupMixin],

  props: {
    listId: {
      type: String,
      required: true,
    },
    inputPlaceholder: {
      type: String,
      default: '',
    },
    submitButtonText: {
      type: String,
      required: true,
    },
    successMessage: {
      type: String,
      default: '',
    },
    errorMessage: {
      type: String,
      default: '',
    },
    theme: {
      type: String,
      default: THEMES.light,
      validator(value) {
        return includesValue(THEMES, value)
      },
    },
  },

  data() {
    return {
      email: '',
      BUTTON_STYLES,
    }
  },

  computed: {
    formData() {
      return {
        listId: this.listId,
        email: this.email,
        source: SOURCES.popup,
      }
    },
    buttonColor() {
      return this.theme === THEMES.light ? BUTTON_STYLES.color.black : BUTTON_STYLES.color.white
    },
    errorMessageClasses() {
      return [
        'newsletter-signup-from__request-msg',
        'newsletter-signup-from__request-msg--error',
        {
          'newsletter-signup-from__request-msg--transparent': this.$data
            .$newsletterSignup_hideErrorMessage,
        },
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
$half-gutter: $site-gutter / 2;
$input-width: 30vw;
$input-height: 50px;
$input-height-medium: 46px;
$max-width: 420px;

.newsletter-signup-from {
  &__newsletter-wrapper {
    margin-top: $half-gutter;
  }

  &__request-msg,
  &__newsletter-form {
    overflow: hidden;
    transition: height $transition-time-slow;
  }

  &__request-msg {
    overflow: hidden;
    padding: $half-gutter 0;
    line-height: $line-height;
    font-size: $font-size-normal;
    transition: height $transition-time-slow;

    &--success {
      color: $color-green;
    }

    &--error {
      color: $color-red;
    }

    &--transparent {
      opacity: 0;
    }
  }

  &__input-wrap {
    padding: 20px 0 10px;
  }

  &__gender-wrap {
    padding: 0 0 10px;
  }

  &__input {
    $line-height: round($font-size-normal * 1.2);
    $padding-vert-medium: ($input-height-medium - $line-height - 2) / 2;
    $padding-vert: ($input-height - $line-height - 2) / 2;
    background: transparent;

    padding-top: ceil($padding-vert-medium) + 1;
    padding-right: $site-gutter;
    padding-bottom: floor($padding-vert-medium) - 1;
    padding-left: 16px;
    width: 100%;
    max-width: $max-width;
    line-height: $line-height;
    font-size: $font-size-normal;
    vertical-align: top;

    &::placeholder {
      font-family: $font-family-body;
      font-style: normal;
    }

    &--light {
      color: $color-grey-dark-2;
      border: 1px solid $color-grey-light-1;

      &::placeholder {
        color: $color-grey-mid-2;
      }
    }

    &--dark {
      color: $color-white;
      border: 1px solid $color-white;

      &::placeholder {
        color: $color-white;
      }
    }

    @include min-screen($mq-width-large) {
      padding-top: ceil($padding-vert) + 1;
      padding-bottom: floor($padding-vert) - 1;
    }

    @media ($mq-medium) and ($mq-height-small) {
      .newsletter-subscribe-overlay--bottom & {
        margin: 0;
        min-width: 250px;
        width: $input-width;

        @include min-screen($mq-width-high) {
          width: $mq-width-high * ($input-width / 100vw);
        }
      }
    }
  }

  &__cta-wrap {
    margin: 0 auto;
    max-width: $max-width;
  }
}
</style>
